<template>
  <b-container fluid>
    <div v-if="isBusy" class="loader"></div>
    <template v-else-if="transfer">
      <b-row class="page-heading">
        <b-col>
          <h1>
            Transfert {{ transfer.transfer_number }}
            <b-badge :variant="statusVariant" class="align-middle ml-2">{{ transfer.status ? transfer.status.name : "—" }}</b-badge>
          </h1>
          <p class="text-muted mb-0">Créé le {{ transfer.created_at }}</p>
        </b-col>
        <b-col cols="auto" class="align-self-center">
          <template v-if="isSent">
            <b-button v-if="$root.isAuthorized('warehouse-transfer_edit')" variant="success" @click="action('receive')"><i class="far fa-check"></i> Réceptionner</b-button>
            <b-button v-if="$root.isAuthorized('warehouse-transfer_delete')" variant="outline-danger" @click="action('cancel')"><i class="far fa-ban"></i> Annuler</b-button>
          </template>
          <router-link :to="{ name: 'WarehouseTransferList' }" class="btn btn-white"><i class="far fa-arrow-left"></i> Retour</router-link>
        </b-col>
      </b-row>

      <!-- Itinéraire -->
      <b-row>
        <b-col>
          <div class="ibox">
            <div class="ibox-content">
              <div class="wt-route">
                <div class="wt-route__node">
                  <span class="wt-route__label">Source</span>
                  <span class="wt-route__name"><i class="far fa-warehouse"></i> {{ transfer.source_warehouse ? transfer.source_warehouse.name : "—" }}</span>
                  <span class="wt-route__code" v-if="transfer.source_warehouse">{{ transfer.source_warehouse.internal_code }}</span>
                </div>
                <div class="wt-route__arrow">
                  <i class="far fa-long-arrow-right"></i>
                  <small>{{ transfer.lines ? transfer.lines.length : 0 }} ligne(s)</small>
                </div>
                <div class="wt-route__node wt-route__node--dest">
                  <span class="wt-route__label">Destination</span>
                  <span class="wt-route__name"><i class="far fa-warehouse"></i> {{ transfer.target_warehouse ? transfer.target_warehouse.name : "—" }}</span>
                  <span class="wt-route__code" v-if="transfer.target_warehouse">{{ transfer.target_warehouse.internal_code }}</span>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <!-- Lignes -->
        <b-col lg="8">
          <div class="ibox">
            <div class="ibox-title"><i class="far fa-boxes"></i> Articles transférés</div>
            <div class="ibox-content">
              <div v-if="!transfer.lines || !transfer.lines.length" class="text-muted">Aucune ligne.</div>
              <div v-for="(line, index) in transfer.lines" :key="line.id || index" class="wt-item">
                <b-badge :variant="line.type === 'reference' ? 'info' : 'primary'" class="wt-item__type">
                  {{ line.type === "reference" ? "Référence" : "Produit" }}
                </b-badge>
                <span class="wt-item__name">
                  <template v-if="line.type === 'reference'">
                    <router-link
                      v-if="line.reference && $root.isAuthorized('reference_show')"
                      :to="{ name: 'ReferenceDetail', params: { id: line.reference.id } }"
                    >{{ line.reference.internal_code }}</router-link>
                    <span v-else>{{ line.reference ? line.reference.internal_code : "—" }}</span>
                  </template>
                  <template v-else>
                    <router-link
                      v-if="line.product && $root.isAuthorized('product_show')"
                      :to="{ name: 'ProductDetail', params: { id: line.product.id } }"
                    >{{ (line.product.internal_code ? line.product.internal_code + " - " : "") + line.product.name }}</router-link>
                    <span v-else>{{ line.product ? ((line.product.internal_code ? line.product.internal_code + " - " : "") + line.product.name) : "—" }}</span>
                  </template>
                </span>
                <span class="wt-item__qty">
                  ×&nbsp;{{ line.quantity }}
                  <b-badge v-if="line.remaining_quantity === 0" variant="success" class="ml-2">Réceptionné</b-badge>
                  <b-badge v-else-if="line.received_quantity > 0" variant="info" class="ml-2">
                    {{ line.received_quantity }} reçu(s), {{ line.remaining_quantity }} en transit
                  </b-badge>
                  <small v-if="line.received_by" class="wt-item__by">
                    par {{ line.received_by.name }}<template v-if="line.received_at"> le {{ line.received_at }}</template>
                  </small>
                </span>
              </div>
            </div>
          </div>
        </b-col>

        <!-- Suivi -->
        <b-col lg="4">
          <div class="ibox">
            <div class="ibox-title"><i class="far fa-history"></i> Suivi</div>
            <div class="ibox-content">
              <ul class="wt-timeline">
                <li class="is-done">
                  <span class="wt-timeline__dot"></span>
                  <span class="wt-timeline__title">Envoyé</span>
                  <span class="wt-timeline__date">{{ transfer.created_at }}</span>
                </li>
                <li v-if="transfer.received_at" class="is-done is-success">
                  <span class="wt-timeline__dot"></span>
                  <span class="wt-timeline__title">Réceptionné</span>
                  <span class="wt-timeline__date">{{ transfer.received_at }}</span>
                  <span class="wt-timeline__by" v-if="transfer.received_by">
                    <i class="far fa-user"></i> {{ transfer.received_by.name }}
                  </span>
                </li>
                <li v-else-if="transfer.received_by" class="is-done is-partial">
                  <span class="wt-timeline__dot"></span>
                  <span class="wt-timeline__title">Réception en cours</span>
                  <span class="wt-timeline__date">{{ lastLineReceivedAt }}</span>
                  <span class="wt-timeline__by">
                    <i class="far fa-user"></i> {{ transfer.received_by.name }}
                  </span>
                </li>
                <li v-if="transfer.cancelled_at" class="is-done is-cancel">
                  <span class="wt-timeline__dot"></span>
                  <span class="wt-timeline__title">Annulé</span>
                  <span class="wt-timeline__date">{{ transfer.cancelled_at }}</span>
                </li>
                <li v-else-if="!transfer.received_at" class="is-pending">
                  <span class="wt-timeline__dot"></span>
                  <span class="wt-timeline__title">En attente de réception</span>
                </li>
              </ul>

              <template v-if="transfer.comment">
                <hr />
                <h5 class="text-muted">Commentaire</h5>
                <p class="mb-0">{{ transfer.comment }}</p>
              </template>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Récapitulatif des pointages : utile dès qu'une réception est partielle
           ou partagée entre plusieurs préparateurs. -->
      <b-row v-if="receptions.length">
        <b-col>
          <div class="ibox">
            <div class="ibox-title">
              <i class="far fa-clipboard-check"></i> Détail des réceptions
              <small class="text-muted ml-2">
                {{ totalReceived }} / {{ totalExpected }} article(s) réceptionné(s)
              </small>
            </div>
            <div class="ibox-content">
              <b-table :items="receptions" :fields="receptionFields" small striped>
                <template slot="label" slot-scope="data">
                  {{ data.item.label || "—" }}
                  <div class="text-muted">{{ data.item.internal_code }}</div>
                </template>
                <template slot="type" slot-scope="data">
                  <b-badge :variant="data.value === 'reference' ? 'info' : 'primary'">
                    {{ data.value === "reference" ? "Référence" : "Produit" }}
                  </b-badge>
                </template>
                <template slot="quantity" slot-scope="data">
                  <b-badge variant="success">+{{ data.value }}</b-badge>
                </template>
                <template slot="received_by" slot-scope="data">
                  <i class="far fa-user"></i>
                  {{ data.value ? data.value.name : "—" }}
                </template>
              </b-table>

              <p v-if="remainingCount" class="text-muted mb-0">
                <i class="far fa-clock"></i>
                {{ remainingCount }} article(s) encore en transit.
              </p>
            </div>
          </div>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
export default {
  name: "WarehouseTransferDetail",
  data() {
    return {
      isBusy: false,
      transfer: null,
      receptionFields: [
        { key: "received_at", label: "Date" },
        { key: "label", label: "Matériel" },
        { key: "type", label: "Type" },
        { key: "quantity", label: "Quantité reçue" },
        { key: "received_by", label: "Réceptionné par" },
      ],
    };
  },
  computed: {
    receptions() {
      return (this.transfer && this.transfer.receptions) || [];
    },
    totalReceived() {
      return ((this.transfer && this.transfer.lines) || []).reduce(
        (total, line) => total + line.received_quantity,
        0
      );
    },
    totalExpected() {
      return ((this.transfer && this.transfer.lines) || []).reduce(
        (total, line) => total + line.quantity,
        0
      );
    },
    remainingCount() {
      return this.totalExpected - this.totalReceived;
    },
    // Un transfert partiellement réceptionné reste actionnable sur son reliquat.
    isSent() {
      return (
        this.transfer &&
        this.transfer.status &&
        ["warehouse-transfer-envoye", "warehouse-transfer-receptionne-partiellement"].indexOf(
          this.transfer.status.slug
        ) >= 0
      );
    },
    // Date du dernier pointage, pour une réception encore incomplète.
    lastLineReceivedAt() {
      const dates = ((this.transfer && this.transfer.lines) || [])
        .filter((line) => line.received_at)
        .map((line) => line.received_at);
      return dates.length ? dates[dates.length - 1] : null;
    },
    statusVariant() {
      if (!this.transfer || !this.transfer.status) return "secondary";
      if (this.transfer.status.slug === "warehouse-transfer-envoye") return "warning";
      if (this.transfer.status.slug === "warehouse-transfer-receptionne-partiellement") return "info";
      if (this.transfer.status.slug === "warehouse-transfer-receptionne") return "success";
      return "secondary";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.isBusy = true;
      this.$http
        .get(process.env.BACK_API_URL + "warehouse-transfer/" + this.$route.params.id)
        .then((response) => {
          this.transfer = response.data;
          this.isBusy = false;
        })
        .catch(() => {
          this.isBusy = false;
          this.$toastr.error("Transfert introuvable.");
        });
    },
    action(type) {
      this.$http
        .patch(process.env.BACK_API_URL + "warehouse-transfer/" + this.transfer.id + "/" + type)
        .then((response) => {
          this.transfer = response.data;
          this.$toastr.success(type === "receive" ? "Transfert réceptionné." : "Transfert annulé.");
        })
        .catch((error) => {
          this.$toastr.error((error.response && error.response.data && error.response.data.message) || "Action impossible.");
        });
    },
  },
};
</script>

<style scoped>
/* Itinéraire */
.wt-route {
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex-wrap: wrap;
}
.wt-route__node {
  flex: 1;
  min-width: 200px;
  padding: 18px 22px;
  border: 1px solid #e7eaec;
  border-left: 4px solid #1ab394;
  border-radius: 4px;
  background: #fafbfc;
}
.wt-route__node--dest {
  border-left-color: #1c84c6;
}
.wt-route__label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  color: #909499;
  margin-bottom: 6px;
}
.wt-route__name {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: #34495e;
}
.wt-route__code {
  display: block;
  font-size: 12px;
  color: #909499;
  margin-top: 2px;
}
.wt-route__arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  color: #b3b9bf;
}
.wt-route__arrow i {
  font-size: 26px;
}
.wt-route__arrow small {
  margin-top: 4px;
  font-size: 11px;
}

/* Lignes */
.wt-item {
  display: flex;
  align-items: center;
  padding: 10px 4px;
  border-bottom: 1px solid #f1f3f5;
}
.wt-item:last-child {
  border-bottom: none;
}
.wt-item__type {
  width: 84px;
  text-align: center;
  margin-right: 12px;
}
.wt-item__name {
  flex: 1;
  color: #34495e;
  font-size: 15px;
  font-weight: 500;
}
.wt-item__qty {
  font-weight: 600;
  color: #34495e;
  white-space: nowrap;
  text-align: right;
}
.wt-item__by {
  display: block;
  font-weight: 400;
  font-size: 11px;
  color: #909499;
}

/* Timeline */
.wt-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}
.wt-timeline li {
  position: relative;
  padding: 0 0 18px 22px;
  border-left: 2px solid #e7eaec;
}
.wt-timeline li:last-child {
  border-left-color: transparent;
  padding-bottom: 0;
}
.wt-timeline__dot {
  position: absolute;
  left: -7px;
  top: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #b3b9bf;
}
.wt-timeline li.is-done .wt-timeline__dot {
  background: #1ab394;
  border-color: #1ab394;
}
.wt-timeline li.is-success .wt-timeline__dot {
  background: #1c84c6;
  border-color: #1c84c6;
}
.wt-timeline li.is-partial .wt-timeline__dot {
  background: #f8ac59;
  border-color: #f8ac59;
}
.wt-timeline__by {
  display: block;
  font-size: 12px;
  color: #909499;
}
.wt-timeline li.is-cancel .wt-timeline__dot {
  background: #ed5565;
  border-color: #ed5565;
}
.wt-timeline__title {
  display: block;
  font-weight: 600;
  color: #34495e;
}
.wt-timeline__date {
  display: block;
  font-size: 12px;
  color: #909499;
}
.wt-timeline li.is-pending .wt-timeline__title {
  color: #909499;
  font-weight: 400;
}
</style>



// WEBPACK FOOTER //
// src/components/WarehouseTransfer/WarehouseTransferDetail.vue